"use client";

import { ChevronRight, Eye } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { toast } from "sonner";
import { reviewMyPreviewJoinRequest } from "@/actions/event-join-request";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import Link from "@/components/ui/link";
import type { PreviewGuide } from "@/lib/events/preview/guide";
import { uiHaptics } from "@/lib/ui-haptics";

const dateLabel = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });

const REQUEST_STATUS_LABEL: Record<string, string> = {
  pending: "Waiting on the host",
  approved: "Approved",
  declined: "Declined",
  withdrawn: "Withdrawn",
  canceled: "Closed",
};

/**
 * The preview's own chrome: a "Preview" pill on every page of a preview copy
 * and the guide behind it. The guide lists each flow this event has, and lets
 * the visitor play the host on their own requests, since on a preview there
 * is no host to wait for.
 */
export function EventPreviewBar({
  guide,
  signedIn,
}: {
  guide: PreviewGuide;
  signedIn: boolean;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [, startTransition] = useTransition();

  const decide = (
    requestId: number,
    decision: "approve" | "decline" | "reopen",
  ) => {
    setPendingId(requestId);
    startTransition(async () => {
      const result = await reviewMyPreviewJoinRequest({ requestId, decision });
      setPendingId(null);
      if (!result.ok) {
        uiHaptics.error();
        toast.error(result.message);
        return;
      }
      uiHaptics.success();
      toast.success(result.message ?? "Done.");
      router.refresh();
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          uiHaptics.select();
          setOpen(true);
        }}
        // Bottom-right, clear of every page's own header (back buttons, titles)
        // and sitting just above the fixed booking and pay bars.
        className="fixed right-3 bottom-[calc(env(safe-area-inset-bottom)+104px)] z-[1050] flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-50/90 px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm backdrop-blur-md backdrop-saturate-150 transition-transform duration-150 ease-[var(--ease-out-strong)] active:scale-[0.97] dark:border-amber-300/30 dark:bg-amber-950/70 dark:text-amber-100"
        aria-label="Open the preview guide"
      >
        <Eye className="size-3.5" aria-hidden />
        Preview
        <span className="font-normal opacity-70">· Guide</span>
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent
          // Above the event page's fixed footers (1000), below stories (1090).
          layer={1060}
          className="mx-auto max-h-[86dvh] max-w-[560px] rounded-t-[30px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#090b10] dark:text-white"
        >
          <DrawerHeader className="px-5 pb-2 text-left">
            <DrawerTitle className="text-2xl font-semibold text-pretty">
              Preview of {guide.eventName}
            </DrawerTitle>
            <DrawerDescription className="text-sm text-text-secondary dark:text-white/60">
              Everything works up to payment, which is simulated. Nothing you do
              here touches the real event, and nobody at {guide.hiiveName} is
              notified.
            </DrawerDescription>
          </DrawerHeader>

          <div className="space-y-6 overflow-y-auto px-5 pb-[calc(env(safe-area-inset-bottom)+20px)]">
            {!signedIn ? (
              <p className="rounded-2xl border border-border bg-surface-alt px-4 py-3 text-sm text-text-secondary dark:border-white/10 dark:bg-white/[0.04] dark:text-white/70">
                You're seeing it signed out. Sign in or create an account when
                the flow asks, to see what a signed-in guest sees.
              </p>
            ) : null}

            <section>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-text-tertiary uppercase dark:text-white/45">
                Try each flow
              </h3>
              <ul className="overflow-hidden rounded-2xl border border-border dark:border-white/10">
                {guide.flows.map((flow, index) => (
                  <li
                    key={flow.key}
                    className={
                      index > 0
                        ? "border-t border-border dark:border-white/10"
                        : undefined
                    }
                  >
                    {flow.href ? (
                      <Link
                        href={flow.href}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-alt dark:hover:bg-white/[0.04]"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">
                            {flow.title}
                          </span>
                          <span className="block text-xs text-text-secondary dark:text-white/55">
                            {flow.detail}
                          </span>
                        </span>
                        <ChevronRight
                          className="size-4 shrink-0 text-text-tertiary"
                          aria-hidden
                        />
                      </Link>
                    ) : (
                      <div className="px-4 py-3">
                        <span className="block text-sm font-medium">
                          {flow.title}
                        </span>
                        <span className="block text-xs text-text-secondary dark:text-white/55">
                          {flow.detail}
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {guide.requests.length > 0 ? (
              <section>
                <h3 className="mb-1 text-xs font-semibold tracking-wide text-text-tertiary uppercase dark:text-white/45">
                  Your requests: decide as the host
                </h3>
                <p className="mb-2 text-xs text-text-secondary dark:text-white/55">
                  On the real event the organiser does this from their
                  dashboard.
                </p>
                <ul className="space-y-2">
                  {guide.requests.map((request) => {
                    const needsHold =
                      request.cardHold &&
                      request.status === "pending" &&
                      !request.holdPlaced;
                    return (
                      <li
                        key={request.id}
                        className="rounded-2xl border border-border px-4 py-3 dark:border-white/10"
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium">
                            {request.ticketName}
                          </span>
                          <span className="text-xs text-text-secondary dark:text-white/55">
                            {REQUEST_STATUS_LABEL[request.status] ??
                              request.status}
                          </span>
                        </div>
                        <div className="text-xs text-text-tertiary dark:text-white/45">
                          {dateLabel(request.startTimeIso)}
                        </div>
                        {needsHold ? (
                          <p className="mt-2 text-xs text-text-secondary dark:text-white/60">
                            Hold your card first: finish the request, then use
                            "Simulate card hold" at checkout.
                          </p>
                        ) : request.status === "pending" ? (
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              disabled={pendingId === request.id}
                              onClick={() => decide(request.id, "approve")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={pendingId === request.id}
                              onClick={() => decide(request.id, "decline")}
                            >
                              Decline
                            </Button>
                          </div>
                        ) : request.status === "declined" ? (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={pendingId === request.id}
                              onClick={() => decide(request.id, "reopen")}
                            >
                              Reopen
                            </Button>
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {guide.bookings.length > 0 || guide.waitlistCount > 0 ? (
              <section>
                <h3 className="mb-2 text-xs font-semibold tracking-wide text-text-tertiary uppercase dark:text-white/45">
                  Your preview bookings
                </h3>
                <ul className="space-y-2">
                  {guide.bookings.map((booking) => (
                    <li key={booking.id}>
                      <Link
                        href={
                          booking.status === "confirmed"
                            ? `/wallet/events/${booking.id}`
                            : `${guide.eventPath}/book/payment?bookingId=${booking.id}`
                        }
                        onClick={() => setOpen(false)}
                        className="flex items-center justify-between rounded-2xl border border-border px-4 py-3 text-sm transition-colors hover:bg-surface-alt dark:border-white/10 dark:hover:bg-white/[0.04]"
                      >
                        <span>
                          {booking.status === "confirmed"
                            ? "Ticket"
                            : "Checkout in progress"}{" "}
                          <span className="text-text-tertiary dark:text-white/45">
                            · {dateLabel(booking.startTimeIso)}
                          </span>
                        </span>
                        <ChevronRight
                          className="size-4 text-text-tertiary"
                          aria-hidden
                        />
                      </Link>
                    </li>
                  ))}
                  {guide.waitlistCount > 0 ? (
                    <li className="rounded-2xl border border-border px-4 py-3 text-sm dark:border-white/10">
                      On {guide.waitlistCount} waitlist
                      {guide.waitlistCount === 1 ? "" : "s"}
                    </li>
                  ) : null}
                </ul>
              </section>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
